import React, { useContext, useEffect } from 'react'
import { LoadingLogo } from '@labrav/react-components'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { LoadingTypes } from '../../modules/loading/reducer'
import { PartnerType } from '../../types/partner'
import { setTelemetryAuthType } from '../../telemetry/telemetryContext'
import { isLoading as getIsLoading } from '../../utils/loadingState'
import type { ComponentLoadingState } from '../../modules/loading/reducer'
import { PartnerAccountDataActionsContext } from './PartnerAccountDataActionsContext'
import type { PartnerAccountDataActions } from './types'

export interface PartnerAccountDataProviderProps {
  children: React.ReactNode
  isExternal?: boolean
  /** Override actions from context when provided */
  actions?: PartnerAccountDataActions
  loadingFallback?: React.ReactNode
}

type PartnerAccountDataRootState = {
  loading: ComponentLoadingState
}

export const PartnerAccountDataProvider: React.FC<
  PartnerAccountDataProviderProps
> = ({
  children,
  isExternal = false,
  actions: actionsOverride,
  loadingFallback = <LoadingLogo />,
}) => {
  const contextActions = useContext(PartnerAccountDataActionsContext)
  const actions: PartnerAccountDataActions | null =
    actionsOverride ?? contextActions
  const dispatch = useDispatch()
  const { partnerId } = useParams<{
    partnerId?: string
  }>()

  const accountsLoading = useSelector((state: PartnerAccountDataRootState) =>
    getIsLoading(state.loading)(LoadingTypes.LOAD_AVAILABLE_ACCOUNTS)
  )

  useEffect(() => {
    if (partnerId && isExternal && actions) {
      setTelemetryAuthType('external-app')
      dispatch(actions.loadAvailableAccounts(partnerId) as never)
      dispatch(actions.getAccount(partnerId, PartnerType.User) as never)
    }
  }, [partnerId, isExternal, dispatch, actions])

  if (actions && accountsLoading) {
    return <>{loadingFallback}</>
  }

  return <>{children}</>
}
